const BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000/api';

let globalQueryTracker = null;

export const setGlobalQueryTracker = (tracker) => {
  globalQueryTracker = tracker;
};

const fetcher = async (url, type = 'json', method = 'GET', options = {}) => {
  const response = await fetch(`${BASE_URL}/${url}`, {
    method,
    credentials: 'include',
    ...options,
  });

  const extractApiErrorMessage = async (res) => {
    const contentType = res.headers.get('content-type') || '';
    try {
      if (contentType.includes('application/json')) {
        const errorJson = await res.json();
        if (typeof errorJson === 'string') return errorJson;
        if (errorJson && typeof errorJson.message === 'string') return errorJson.message;
        return JSON.stringify(errorJson);
      }
      if (contentType.includes('text/html')) {
        return `${res.status} ${res.statusText || 'External service error'}`;
      }
      return await res.text();
    } catch (_err) {
      return res.statusText || 'Request failed';
    }
  };

  if (!response.ok) {
    const errorMessage = await extractApiErrorMessage(response);
    const error = new Error(errorMessage || `API Error: ${response.status} ${response.statusText}`);
    error.status = response.status;
    throw error;
  }

  return type === 'json' ? response.json() : response.text();
};

const buildQueryParams = (params = {}) => {
  const queryParams = new URLSearchParams();

  if (params.region && params.region !== 'all') queryParams.set('region', params.region);
  if (params.multi && params.multi !== 'all') queryParams.set('multi', params.multi);
  if (params.outdated) queryParams.set('outdated', params.outdated);
  if (params.fixed) queryParams.set('fixed', params.fixed);
  if (params.severity) queryParams.set('severity', params.severity);
  if (params.with_vulns) queryParams.set('with_vulns', params.with_vulns);
  if (params.export) queryParams.set('export', params.export);
  if (params.offset !== undefined) queryParams.set('offset', params.offset);
  if (params.limit !== undefined) queryParams.set('limit', params.limit);
  if (params.kw) queryParams.set('kw', params.kw);
  if (params.team && params.team !== 'all') queryParams.set('team', params.team);
  if (params.state) queryParams.set('state', params.state);
  if (params.codename) queryParams.set('codename', params.codename);
  if (params.codename_exclude) queryParams.set('codename_exclude', params.codename_exclude);
  if (params.new_hosts) queryParams.set('new_hosts', params.new_hosts);
  if (params.version) queryParams.set('version', params.version);
  if (params.package_name) queryParams.set('package_name', params.package_name);
  if (params.project && params.project !== 'all') queryParams.set('project', params.project);
  if (params.admin && params.admin !== 'all') queryParams.set('admin', params.admin);

  return queryParams.toString();
};

/**
 * Wraps paginated API calls. Handles:
 * - limit/offset injection
 * - query tracking (track: false keeps a call out of the export panel's "This list" query)
 * - unwrapping { data, total, limit, offset } response into { data, total, hasMore }
 */
const withPagination = (apiFunction, endpointName) => {
  return async (args = {}) => {
    const { limit = 30, offset = 0, kw = '', track = true, ...rest } = args;

    if (globalQueryTracker && track && !args.export) {
      globalQueryTracker({
        endpoint: endpointName,
        params: { ...args, limit, offset, kw },
      });
    }

    const result = await apiFunction({ ...rest, limit, offset, kw });

    if (result && typeof result === 'object' && 'data' in result) {
      return {
        data: result.data,
        total: result.total,
        hasMore: offset + result.data.length < result.total,
      };
    }

    return result;
  };
};

// ─── Hosts ────────────────────────────────────────────────────────────────────

const getHosts = withPagination(async (params) => {
  const queryString = buildQueryParams({ state: 'active', ...params });
  const responseType = params.export === 'csv' ? 'text' : 'json';
  return fetcher(`hosts?${queryString}`, responseType);
}, 'hosts');

const getHost = async (id) => fetcher(`hosts/${id}`);

// One row per host with active findings for a CVE; kw filters by hostname, team by team_flow.
const getAffectedHostsForCve = withPagination(async ({ cve, ...params }) => {
  const queryString = buildQueryParams(params);
  return fetcher(`hosts/for_vuln/${encodeURIComponent(cve)}?${queryString}`);
}, 'affected_hosts_for_cve');

const getHostOwnerships = withPagination(async (params) => {
  const queryString = buildQueryParams(params);
  return fetcher(`hosts/ownership?${queryString}`);
}, 'host_ownerships');

const updateHostOwnership = async (payload = {}) => {
  return fetcher('hosts/ownership', 'text', 'PUT', {
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
};

const getOwnershipOptions = async () => fetcher('hosts/ownership/options');

const getOsNames = async () => {
  try {
    const response = await fetcher('hosts/os_names');
    return Array.isArray(response) ? response : [];
  } catch (error) {
    console.error('Error in getOsNames:', error);
    return [];
  }
};

// ─── Packages ─────────────────────────────────────────────────────────────────

const getPackages = withPagination(async (params) => {
  const queryString = buildQueryParams(params);
  const responseType = params.export === 'csv' ? 'text' : 'json';
  return fetcher(`packages?${queryString}`, responseType);
}, 'packages');

const getPackage = async (id) => fetcher(`packages/${id}`);

// ─── Vulnerabilities ──────────────────────────────────────────────────────────

const getVulns = withPagination(async (params) => {
  const queryString = buildQueryParams(params);
  const responseType = params.export === 'csv' ? 'text' : 'json';
  return fetcher(`vulns?${queryString}`, responseType);
}, 'vulnerabilities');

// One row per host + package finding for a CVE; kw filters by package name.
const getVulnFindings = withPagination(async ({ cve, ...params }) => {
  const queryString = buildQueryParams(params);
  return fetcher(`vulns/${encodeURIComponent(cve)}?${queryString}`);
}, 'vuln_findings');

const getVulnsForHost = withPagination(async ({ hostId, ...params }) => {
  const queryString = buildQueryParams(params);
  const responseType = params.export === 'csv' ? 'text' : 'json';
  return fetcher(`vulns/for_host/${hostId}?${queryString}`, responseType);
}, 'vulns_for_host');

const getVulnsForPackage = withPagination(async ({ packageId, ...params }) => {
  const queryString = buildQueryParams(params);
  const responseType = params.export === 'csv' ? 'text' : 'json';
  return fetcher(`vulns/for_package/${packageId}?${queryString}`, responseType);
}, 'vulns_for_package');

// ─── Findings ─────────────────────────────────────────────────────────────────

// One row per host + package + CVE, with the open ticket holding it. Needs hostId, cve or packageId.
const getFindings = withPagination(async ({ hostId, cve, packageId, status, ticketed, ...params }) => {
  const query = new URLSearchParams(buildQueryParams(params));
  if (hostId !== undefined && hostId !== null) query.set('host_id', hostId);
  if (cve) query.set('cve', cve);
  if (packageId !== undefined && packageId !== null) query.set('package_id', packageId);
  if (status) query.set('status', status);
  if (ticketed) query.set('ticketed', ticketed);
  return fetcher(`findings?${query.toString()}`);
}, 'findings');

// ─── Inventory ────────────────────────────────────────────────────────────────

const getPackagesForHost = withPagination(async ({ hostId, ...params }) => {
  const queryString = buildQueryParams(params);
  const responseType = params.export === 'csv' ? 'text' : 'json';
  return fetcher(`inventory/packages_for_host/${hostId}?${queryString}`, responseType);
}, 'packages_for_host');

const getHostsForPackage = withPagination(async ({ packageId, ...params }) => {
  const queryString = buildQueryParams(params);
  const responseType = params.export === 'csv' ? 'text' : 'json';
  return fetcher(`inventory/hosts_for_package/${packageId}?${queryString}`, responseType);
}, 'hosts_for_package');

const getVersionsForPackage = withPagination(async ({ packageName, ...params }) => {
  const queryString = buildQueryParams(params);
  const responseType = params.export === 'csv' ? 'text' : 'json';
  return fetcher(`inventory/versions_for_package/${packageName}?${queryString}`, responseType);
}, 'versions_for_package');

const getHostCycloneDX = async (hostname) => fetcher(`inventory/host/${hostname}`);

// ─── Statistics ───────────────────────────────────────────────────────────────

const getDashboardStatistics = async (team) => {
  try {
    const endpoint = team ? `statistics/dashboard?team=${encodeURIComponent(team)}` : 'statistics/dashboard';
    return await fetcher(endpoint);
  } catch (error) {
    console.error('Error in getDashboardStatistics:', error);
    return null;
  }
};

// ─── Authentication ───────────────────────────────────────────────────────────

const getOAuth = async () => fetcher('oauth');
const getIsAuth = async () => fetcher('auth/session');

// ─── Tickets ──────────────────────────────────────────────────────────────────

const getTickets = async (ticketType, options = {}) => {
  try {
    const queryParams = new URLSearchParams();
    if (typeof options.preview_mode === 'boolean') queryParams.set('preview_mode', options.preview_mode.toString());
    if (options.priority) queryParams.set('priority', options.priority);
    if (Number.isFinite(options.ticket_limit)) queryParams.set('ticket_limit', options.ticket_limit.toString());
    if (Number.isFinite(options.max_ticket_size)) queryParams.set('max_ticket_size', options.max_ticket_size.toString());
    if (options.severity) queryParams.set('severity', options.severity);
    if (options.from_date) queryParams.set('from_date', options.from_date);
    if (options.status) queryParams.set('status', options.status);
    if (options.mode) queryParams.set('mode', options.mode);
    if (options.flow_type) queryParams.set('flow_type', options.flow_type);
    if (options.kw) queryParams.set('kw', options.kw);
    if (options.created_on) queryParams.set('created_on', options.created_on);
    if (options.hostname) queryParams.set('hostname', options.hostname);
    if (options.cve) queryParams.set('cve', options.cve);
    if (options.package) queryParams.set('package', options.package);
    if (Number.isFinite(options.offset)) queryParams.set('offset', options.offset.toString());
    if (Number.isFinite(options.limit)) queryParams.set('limit', options.limit.toString());

    const endpoint = ticketType ? `tickets/${ticketType}` : 'tickets';
    const queryString = queryParams.toString();
    const url = queryString ? `${endpoint}?${queryString}` : endpoint;
    return await fetcher(url, 'json');
  } catch (error) {
    console.error(`Error in getTickets for ${ticketType}:`, error);
    throw error;
  }
};

const getTicket = async (ticketId) => {
  try {
    return await fetcher(`tickets/${ticketId}`, 'json');
  } catch (error) {
    console.error(`Error in getTicket for ${ticketId}:`, error);
    throw error;
  }
};

const updateTicketStatus = async (ticketId, status) => {
  return fetcher(`tickets/${ticketId}/status`, 'json', 'PUT', {
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ status }),
  });
};

const updateTicket = async (ticketId, fields) => {
  return fetcher(`tickets/${ticketId}`, 'json', 'PUT', {
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(fields),
  });
};

const createTicketFromPreview = async (ticketId) => {
  return fetcher(`tickets/${ticketId}/promote`, 'json', 'POST');
};

const createTicketsFromPreviews = async (ids = []) => {
  return fetcher('tickets/bulk_create_from_previews', 'json', 'POST', {
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ ticket_ids: ids }),
  });
};

const deleteTicket = async (ticketId) => {
  return fetcher(`tickets/${ticketId}`, 'json', 'DELETE');
};

const syncTickets = async () => {
  return fetcher('tickets/sync', 'json', 'POST');
};

const createTickets = async (payload = {}) => {
  const queryParams = new URLSearchParams();
  Object.entries(payload).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      queryParams.set(key, String(value));
    }
  });
  const query = queryParams.toString();
  const normalizedFlowType = String(payload.flow_type || 'dba').toLowerCase();
  const flowType = ['dba', 'devops', 'enterprise'].includes(normalizedFlowType) ? normalizedFlowType : 'dba';
  return fetcher(`tickets/flows/${flowType}${query ? `?${query}` : ''}`, 'json', 'POST');
};

const resolveTeamFlow = (payload = {}) => {
  const normalized = String(payload.team_flow || payload.flow_type || 'dba').toLowerCase();
  return ['dba', 'devops', 'enterprise'].includes(normalized) ? normalized : 'dba';
};

const buildTicketFlowQuery = (payload = {}) => {
  const queryParams = new URLSearchParams();
  Object.entries(payload).forEach(([key, value]) => {
    if (key === 'flow_type') return;
    if (value !== undefined && value !== null && value !== '') {
      queryParams.set(key, String(value));
    }
  });
  queryParams.set('team_flow', resolveTeamFlow(payload));
  return queryParams.toString();
};

const createPackageTickets = async (payload = {}) => {
  const query = buildTicketFlowQuery(payload);
  return fetcher(`tickets/flows/package${query ? `?${query}` : ''}`, 'json', 'POST');
};

const createCveTickets = async (payload = {}) => {
  const query = buildTicketFlowQuery(payload);
  return fetcher(`tickets/flows/cve${query ? `?${query}` : ''}`, 'json', 'POST');
};

// ─── Investigation ────────────────────────────────────────────────────────────

const investigate = async (queryText, options = {}) => {
  const payload = {
    query_text: queryText,
    offset: options.offset || 0,
    limit: options.limit || 50,
  };
  if (options.result_type_override) {
    payload.result_type_override = options.result_type_override;
  }
  return fetcher('investigate', 'json', 'POST', {
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
};

// ─── Cron ─────────────────────────────────────────────────────────────────────

const getCronSchedule = async () => fetcher('cron-scheduling');

const updateCronSchedule = async (payload = {}) => {
  return fetcher('cron-scheduling/update', 'json', 'POST', {
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
};

// ─── Exports ──────────────────────────────────────────────────────────────────

export const API = {
  GET_HOSTS: getHosts,
  GET_HOST: getHost,
  GET_AFFECTED_HOSTS_FOR_CVE: getAffectedHostsForCve,
  GET_HOST_OWNERSHIPS: getHostOwnerships,
  UPDATE_HOST_OWNERSHIP: updateHostOwnership,
  GET_OWNERSHIP_OPTIONS: getOwnershipOptions,
  GET_OS_NAMES: getOsNames,

  GET_PACKAGES: getPackages,
  GET_PACKAGE: getPackage,

  GET_VULNS: getVulns,
  GET_VULN_FINDINGS: getVulnFindings,
  GET_VULNS_FOR_HOST: getVulnsForHost,
  GET_VULNS_FOR_PACKAGE: getVulnsForPackage,

  GET_FINDINGS: getFindings,

  GET_PACKAGES_FOR_HOST: getPackagesForHost,
  GET_HOSTS_FOR_PACKAGE: getHostsForPackage,
  GET_VERSIONS_FOR_PACKAGE: getVersionsForPackage,
  GET_HOST_CYCLONEDX: getHostCycloneDX,

  GET_DASHBOARD_STATISTICS: getDashboardStatistics,

  GET_OAUTH: getOAuth,
  GET_ISAUTH: getIsAuth,

  GET_TICKETS: getTickets,
  GET_TICKET: getTicket,
  UPDATE_TICKET_STATUS: updateTicketStatus,
  UPDATE_TICKET: updateTicket,
  CREATE_TICKET_FROM_PREVIEW: createTicketFromPreview,
  CREATE_TICKETS_FROM_PREVIEWS: createTicketsFromPreviews,
  DELETE_TICKET: deleteTicket,
  SYNC_TICKETS: syncTickets,
  CREATE_TICKETS: createTickets,
  CREATE_PACKAGE_TICKETS: createPackageTickets,
  CREATE_CVE_TICKETS: createCveTickets,

  INVESTIGATE: investigate,

  GET_CRON_SCHEDULE: getCronSchedule,
  UPDATE_CRON_SCHEDULE: updateCronSchedule,
};
